import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Loading from '../utils/Loading';
import Canvas from '../display/Canvas';
import ImageControls from './ImageControls';
import { Icons as PnIcons } from '../payloadNew/Icons';
import { Icons as DnIcons } from './Icons';
import IdChip from '../payloadNew/IdChip';
import TagList from './TagList';
import ReconSummary from './ReconSummary';
import TagsChart from './TagsChart';
import ReportErrorModal from './ReportErrorModal';
import ReportBanner, { pickLatestTicket } from './ReportBanner';
import HistoryDrawer from '../historyDrawer/HistoryDrawer';
import PictureButtons from './PictureButtons';
import { Tour, useTour } from '../tour';
import getDisplayNewSteps from '../tour/steps/displayNewSteps';

import useDidMountEffect from '../../hooks/useDidMountEffect';

import { get_payload_info, set_payload_images } from '../../actions/payloadActions';
import { get_survey_response, get_survey_schema_display, set_survey_images } from '../../actions/surveyActions';
import { get_image_info, get_image_result, get_image_tags } from '../../actions/imageActions';
import { get_result_info, get_result_tags, get_results_history } from '../../actions/resultActions';
import { get_all_projects } from '../../actions/projectsActions';
import { getTicketStatusDesc } from '../../utils/getStatusDesc';
import { get_ticket_reasons, get_ticket_settings, get_my_tickets, get_resolved_tickets, get_ticket_exists, get_ticket_history, reasonsForTarget, ticketsForTarget, existsForTarget, historyForTicket, pickStatusNote } from '../../actions/ticketActions';

import formatString from '../../utils/formatString';
import isEmpty from '../../utils/isEmpty';
import { initElementsTagsList } from '../../utils/initTagsList';
import { resolveSchemaId } from '../../utils/parseSurveyData';
import { fetchImageDataUrl } from '../../utils/loadImageDataUrl';

import './displayNew.css';

const SECTIONS = [
	{ id: 'dn-info', key: 'info', label: 'Info', icon: 'image' },
	{ id: 'dn-recon', key: 'recon', label: 'Recognition', icon: 'tag' },
];

const isCustomValid = (obj) => {
	const expectedKeys = ['text_annotations'];
	if (!obj?.custom) return false;
	return !expectedKeys.some((key) => key in obj.custom);
};

const Stat = ({ label, value, mono }) => (
	<div className='dn-stat'>
		<div className='dn-stat-label'>{label}</div>
		<div className={`dn-stat-value${mono ? ' mono' : ''}`}>{value ?? '—'}</div>
	</div>
);

const DisplayNew = () => {
	const { image_id } = useParams();
	return <DisplayNewInner key={image_id} />;
};

const DisplayNewInner = () => {
	const { t } = useTranslation();
	const { location_id, payload_id, response_id, image_id } = useParams();
	const isSurveyContext = Boolean(response_id);
	const dispatch = useDispatch();

	const { user, isAdminUser } = useSelector((s) => s.auth);
	const { app_config } = useSelector((s) => s.organization);
	const { payload_info, payload_images } = useSelector((s) => s.payload);
	const { survey_response, survey_images, survey_schema_display } = useSelector((s) => s.survey);
	const { image_loading, image_info, image_result, image_result_loading, image_tags_loading, image_tags } = useSelector((s) => s.image);
	const image_error = useSelector((s) => s.image.error);
	const { result_info, result_tags_loading, result_tags, result_history, result_history_loading } = useSelector((s) => s.result);
	const { ticket_reasons, ticket_settings, my_tickets, resolved_tickets, ticket_exists, ticket_history } = useSelector((s) => s.ticket);
	// Scope for the ticket lookups: the organization of the claimed object.
	const ticketOrgId = image_info?.organization?.$oid;
	// Filtered to tickets that claim the image itself, mirroring PayloadNew, so
	// the two banners stay independent whatever the lookup returns alongside.
	// The whole document is kept, not just its status: the QA note is fetched by
	// the ticket's own id.
	const latestResolvedTicket = useMemo(
		() => pickLatestTicket(ticketsForTarget(resolved_tickets?.tickets, 'image')),
		[resolved_tickets]
	);
	const latestResolvedStatus = latestResolvedTicket?.status ?? null;
	const resolvedTicketId = latestResolvedTicket?._id?.$oid ?? null;
	// What QA wrote when it moved the ticket into that state, shown under the
	// generic copy in the banner. Null whenever there is nothing to show — the
	// common case, since the comment is optional across the whole API.
	const resolvedNote = useMemo(
		() => pickStatusNote(historyForTicket(ticket_history, resolvedTicketId), latestResolvedStatus),
		[ticket_history, resolvedTicketId, latestResolvedStatus]
	);
	// See PayloadNew: /mine answers "is it mine?", /exists answers "is this object
	// blocked?" — which is what the banner needs, since a colleague's ticket also
	// blocks the create. Falls back to /mine while the answer is unusable.
	const existsAnswer = useMemo(
		() => existsForTarget(ticket_exists, 'image', image_id),
		[ticket_exists, image_id]
	);
	const hasActiveTicket = useMemo(
		() => (existsAnswer !== null
			? existsAnswer
			: ticketsForTarget(my_tickets?.tickets, 'image').length > 0),
		[existsAnswer, my_tickets]
	);
	// The ticket the history button opens: the live one while it is open, the
	// resolved one afterwards — the same precedence the banner itself renders.
	// A live ticket filed by somebody else is deliberately absent: /exists says
	// the object is blocked but not by which ticket, so there is no id to fetch a
	// log with and the button stays hidden.
	const liveTicket = useMemo(
		() => pickLatestTicket(ticketsForTarget(my_tickets?.tickets, 'image')),
		[my_tickets]
	);
	const trackedTicket = hasActiveTicket ? liveTicket : latestResolvedTicket;
	const trackedTicketId = trackedTicket?._id?.$oid ?? null;
	const ticketHistory = useMemo(
		() => historyForTicket(ticket_history, trackedTicketId),
		[ticket_history, trackedTicketId]
	);
	// Only reasons whose ticket_types bitmask includes `image` count towards
	// showing the banner here — payload-only reasons are irrelevant on a photo.
	const imageReasonsCount = useMemo(
		() => reasonsForTarget(ticket_reasons?.reasons, 'image').length,
		[ticket_reasons]
	);

	// The report window is expressed in days from the image's date; 0 means
	// "no window" per the backend contract. When the image is older than the
	// configured window, we drop the report button and surface an inline
	// notice in its place so the user isn't confused by a backend 400.
	const reportWindow = useMemo(() => {
		const cfg = ticket_settings || {};
		const days = Number.isFinite(cfg.window_days) ? cfg.window_days : 30;
		const imgEpoch = image_info?.date?.$date;
		if (!days || !imgEpoch) return { days, expired: false };
		const ageDays = (Date.now() - imgEpoch) / 86400000;
		return { days, expired: ageDays > days };
	}, [ticket_settings, image_info]);

	const defaultHeight = 600;

	const [image, setImage] = useState('');
	const [imageLoading, setImageLoading] = useState(true);
	const [imageError, setImageError] = useState({ isError: false, msg: '' });
	const [appOrgConfig, setAppOrgConfig] = useState({});

	const [globalTagsOpts, setGlobalTagsOpts] = useState([]);
	const [resourceTagsOpts, setResourceTagsOpts] = useState([]);
	const [resultTypeOpts, setResultTypeOpts] = useState([]);

	const [resultId, setResultId] = useState('');
	const [historyId, setHistoryId] = useState('');
	const [selectedResult, setSelectedResult] = useState({});

	const [recon, setRecon] = useState('');
	const [canvas, setCanvas] = useState(null);
	const [tagsList, setTagsList] = useState([]);
	const [predictions, setPredictions] = useState([]);
	const [photoIndex, setPhotoIndex] = useState(-1);
	const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
	const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
	const [controlsOpen, setControlsOpen] = useState(false);
	const [reportOpen, setReportOpen] = useState(false);
	const [ticketDrawer, setTicketDrawer] = useState(false);

	// A ticket that has only just been opened has a single transition, and a log
	// of one row says nothing the banner does not already say. The button waits
	// until there is a path to show.
	const openTicketHistory = ticketHistory.length > 1
		? () => setTicketDrawer(true)
		: null;
	const tour = useTour({ storageKey: 'display-new-tour-v1', autoStart: true });
	const tourSteps = useMemo(() => getDisplayNewSteps(t), [t]);

	useEffect(() => {
		fetchImageDataUrl(image_id)
			.then((data) => {
				setImage(data);
				setImageLoading(false);
			})
			.catch((err) => {
				setImageError({ isError: true, msg: err.message });
				setImageLoading(false);
			});

		if (payload_id) dispatch(get_payload_info(payload_id));
		if (response_id) dispatch(get_survey_response(response_id));
		dispatch(get_image_info(image_id));
		dispatch(get_image_result(image_id));
		dispatch(get_results_history(image_id));
		dispatch(get_image_tags(user.organization));
		// Is the object blocked, whoever filed it — drives the banner. Takes no
		// organization: the scope comes from the claimed object itself. The two
		// /mine lookups do need it and live in their own effect below.
		dispatch(get_ticket_exists('image', image_id));
		// Effective ticket config (description bounds, report window). Scoped by
		// the token, so it doesn't need to wait for image_info to know the org.
		dispatch(get_ticket_settings());

		const onResize = () => setWidth(window.innerWidth);
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	useEffect(() => {
		if (payload_id) dispatch(set_payload_images(payload_info.data));
	}, [payload_info]);

	useEffect(() => {
		if (response_id && !isEmpty(survey_response)) {
			const schema_id = resolveSchemaId(survey_response);
			if (schema_id) dispatch(get_survey_schema_display(schema_id));
		}
	}, [survey_response]);

	useEffect(() => {
		if (response_id && !isEmpty(survey_response) && survey_schema_display) {
			dispatch(set_survey_images(survey_response, survey_schema_display));
		}
	}, [survey_response, survey_schema_display]);

	// The reasons catalogue is the only ticket lookup that still needs the org
	// id, so it waits for image_info. It feeds the banner gate and the modal's
	// category list. (Settings come from /settings/mine on mount.)
	useEffect(() => {
		if (ticketOrgId) dispatch(get_ticket_reasons(ticketOrgId));
	}, [ticketOrgId]);

	// A ticket is stored under the organization of the object it claims, not the
	// author's, so a Percepthor user reviewing a client's photo has to name that
	// organization or /mine falls back to the token's own and answers empty.
	// Nothing is lost by waiting for it: the banner is gated on the reasons
	// catalogue, which needs the same id.
	useEffect(() => {
		if (!image_id || !ticketOrgId) return;
		// Live tickets on this image (states OPEN=1 and REVIEW=2). Kept as the
		// fallback for when /exists can't be trusted.
		dispatch(get_my_tickets({ image: image_id, organization: ticketOrgId, multiple: '1,2' }));
		// Resolved tickets (APPROVED..ERROR) so the banner can surface the
		// outcome of the last report instead of silently going back to the CTA.
		dispatch(get_resolved_tickets({ image: image_id, organization: ticketOrgId, multiple: '3,4,5,6,7,8' }));
	}, [image_id, ticketOrgId]);

	// Transition log of the resolved ticket, for the QA note in the banner. Only
	// fires once there IS a resolved ticket: with none there is nothing to ask
	// for. Takes no organization — the scope comes from the ticket itself.
	useEffect(() => {
		if (trackedTicketId) dispatch(get_ticket_history(trackedTicketId));
	}, [trackedTicketId]);

	useEffect(() => {
		setGlobalTagsOpts([]);
		setResourceTagsOpts([]);
		if (!isEmpty(image_info) && !isEmpty(image_tags)) {
			image_tags.global.forEach((g) => {
				if ((image_info.tags & g.value) === g.value) setGlobalTagsOpts((p) => [...p, g.name]);
			});
			image_tags.resource.forEach((r) => {
				if ((image_info.org_tags & r.value) === r.value) setResourceTagsOpts((p) => [...p, r.name]);
			});
			const cfg = app_config.find((c) => c.organization.$oid === image_info.organization.$oid);
			setAppOrgConfig(cfg);
		}
	}, [image_info, image_tags, app_config]);

	useDidMountEffect(() => {
		setResultTypeOpts([]);
		if (!isEmpty(image_result)) {
			const opts = [];
			image_result.results.forEach((r) => {
				let name = 'No Result';
				if (r.options?.name) name = formatString(r.options.name.replace(/\./g, ' '));
				else if (r.r_type?.name) name = formatString(r.r_type.name.replace(/\./g, ' '));
				const opt = {
					_id: r.result,
					name,
					r_type_id: r.r_type?._id?.$oid ?? null,
					type: r.r_type?.result_type ?? null,
					result_options: r.options?._id?.$oid ?? null,
				};
				if (opt.r_type_id) opts.push(opt);
			});
			setResultTypeOpts(opts);
			if (opts.length > 0 && resultId === '' && historyId === '') {
				setResultId(opts[0]._id.$oid);
			}
		}
	}, [image_result]);

	useEffect(() => {
		if (resultId !== '') {
			setTagsList([]);
			const found = resultTypeOpts.find((r) => r._id.$oid === resultId);
			setSelectedResult(found);
			dispatch(get_all_projects({
				organization: image_info?.organization?.$oid,
				result: found?.r_type_id,
				...(found?.result_options !== null && { options: found?.result_options }),
			}));
			dispatch(get_result_info(found.r_type_id, resultId));
		}
	}, [resultId]);

	useEffect(() => {
		if (historyId !== '') {
			setTagsList([]);
			const h = result_history.find((rh) => rh._id.$oid === historyId);
			const found = {
				_id: { $oid: h.result.$oid },
				name: h.name,
				r_type_id: h.r_type._id.$oid,
				type: h.r_type.result_type,
				result_options: h.options?._id?.$oid || null,
			};
			setSelectedResult(found);
			dispatch(get_all_projects({
				organization: image_info?.organization?.$oid,
				result: found.r_type_id,
				...(found.result_options !== null && { options: found.result_options }),
			}));
			dispatch(get_result_info(found.r_type_id, found._id.$oid));
		}
	}, [historyId]);

	useEffect(() => {
		if (!isEmpty(selectedResult)) {
			if (selectedResult.result_options) dispatch(get_result_tags(true, selectedResult.result_options));
			else if (selectedResult.r_type_id) dispatch(get_result_tags(false, selectedResult.r_type_id));
		}
	}, [selectedResult]);

	useEffect(() => {
		if (selectedResult.type === 1) setRecon('predictions');
		else if (selectedResult.type === 3) setRecon('elements');
	}, [result_info]);

	useDidMountEffect(() => {
		if (!result_tags_loading) {
			if (recon === 'elements') {
				setPredictions(result_info.elements);
				setTagsList(initElementsTagsList(result_info.elements, result_tags));
			} else if (recon === 'predictions') {
				setPredictions(result_info.predictions);
				setTagsList(initElementsTagsList(result_info.predictions, result_tags));
			} else if (recon === 'extras') {
				setPredictions(result_info.extras);
				setTagsList(initElementsTagsList(result_info.extras, result_tags));
			}
		}
	}, [recon, result_tags_loading, result_tags]);

	const activeImages = isSurveyContext ? survey_images : payload_images;

	const selectPhoto = (delta) => {
		const next = photoIndex + delta;
		const nextImageId = activeImages[next]?.imageId;
		if (!nextImageId) return;
		if (isSurveyContext) {
			window.location = `/surveys/responses/${response_id}/image/${nextImageId}`;
		} else {
			window.location = `/locations/${location_id}/payloads/${payload_id}/image/${nextImageId}`;
		}
	};

	useEffect(() => {
		const idx = activeImages.findIndex((o) => o.imageId === image_id);
		setPhotoIndex(idx);
	}, [activeImages]);

	useEffect(() => {
		const obs = new IntersectionObserver(
			(entries) => {
				entries.forEach((e) => {
					if (e.isIntersecting) setActiveSection(e.target.id);
				});
			},
			{ rootMargin: '-30% 0px -60% 0px' }
		);
		SECTIONS.forEach((s) => {
			const el = document.getElementById(s.id);
			if (el) obs.observe(el);
		});
		return () => obs.disconnect();
	}, []);

	const backHref = isSurveyContext
		? `/surveys/responses/${response_id}`
		: `/locations/${location_id}/payloads/${payload_id}`;

	const hasPrev = photoIndex > 0;
	const hasNext = photoIndex >= 0 && photoIndex < activeImages.length - 1;

	const { heroTitle, heroPhotoChip } = useMemo(() => {
		const current = activeImages[photoIndex];
		const fallback = image_info?.filename || image_id;
		if (!current?.type) return { heroTitle: fallback, heroPhotoChip: null };

		const REPEAT_RE = /^(.+)-(\d+)$/;
		const type = current.type;
		const repeatMatch = type.match(REPEAT_RE);
		const base = repeatMatch ? repeatMatch[1] : type;
		const groupIdx = repeatMatch ? parseInt(repeatMatch[2], 10) : null;

		const groupSet = new Set();
		activeImages.forEach((img) => {
			const m = img.type?.match(REPEAT_RE);
			if (m && m[1] === base) groupSet.add(parseInt(m[2], 10));
		});
		const showGroupIdx = groupIdx !== null && groupSet.size > 1;

		const sameType = activeImages.filter((img) => img.type === type);
		const photoIdxInGroup = sameType.findIndex((img) => img.imageId === image_id);
		const showPhotoIdx = sameType.length > 1 && photoIdxInGroup >= 0;

		const name = formatString(base);
		const title = showGroupIdx ? `${name} #${groupIdx + 1}` : name;
		const photoChip = showPhotoIdx
			? t('display.hero.photoOf', {
				defaultValue: 'Photo {{current}} of {{total}}',
				current: photoIdxInGroup + 1,
				total: sameType.length,
			})
			: null;
		return { heroTitle: title, heroPhotoChip: photoChip };
	}, [activeImages, photoIndex, image_id, image_info, t]);

	if (image_loading || imageLoading) {
		return <Loading msg={t('display.loading')} />;
	}

	return (
		<div id={localStorage.getItem('theme')}>
			<ImageControls open={controlsOpen} onClose={() => setControlsOpen(false)} />
			<div className='display-new'>
				<div className='dn-layout'>
					{/* Sticky rail */}
					<aside className='dn-rail'>
						<div className='dn-rail-title'>{t('display.rail.title', { defaultValue: 'SECTIONS' })}</div>
						<nav className='dn-rail-nav'>
							{SECTIONS.map((s) => {
								const Icon = DnIcons[s.icon];
								return (
									<a
										key={s.id}
										href={`#${s.id}`}
										className={`dn-rail-link${activeSection === s.id ? ' active' : ''}`}
										onClick={() => setActiveSection(s.id)}
									>
										{Icon && <span className='dn-rail-icon'><Icon /></span>}
										<span>{t(`display.rail.${s.key}`, { defaultValue: s.label })}</span>
									</a>
								);
							})}
						</nav>

						<button type='button' className='ds-tour-launcher' onClick={tour.start}>
							<DnIcons.compass /> {t('tour.launcher', { defaultValue: 'Take a tour' })}
						</button>
					</aside>

					<main className='dn-main'>
						{/* Page header */}
						<header className='dn-pageheader'>
							<div className='dn-pageheader-inner'>
								<div className='dn-pageheader-main'>
									<a href={backHref} className='ds-back-link'>
										<DnIcons.arrow style={{ transform: 'rotate(180deg)' }} />
										<span>{t(isSurveyContext ? 'display.returnSurvey' : 'display.returnPayload')}</span>
									</a>
									<div className='dn-pageheader-eyebrow'>
										<DnIcons.scan /> {t('display.recon', { defaultValue: 'Products Recognition' })}
									</div>
									<h1 className='dn-pageheader-title'>
										{heroTitle}
										{heroPhotoChip && (
											<span className='dn-pageheader-photo'>
												<span className='dn-pageheader-sep'>·</span>
												{heroPhotoChip}
											</span>
										)}
									</h1>
								</div>
								{activeImages.length > 0 && (
									<aside className='dn-pageheader-aside'>
										<div className='dn-photo-nav'>
											<button
												type='button'
												className='dn-photo-nav-btn'
												disabled={!hasPrev}
												onClick={() => selectPhoto(-1)}
												aria-label={t('display.photosBtns.previous')}
											>
												<DnIcons.arrow style={{ transform: 'rotate(180deg)' }} />
											</button>
											<span className='dn-photo-nav-info'>
												{photoIndex + 1} / {activeImages.length}
											</span>
											<button
												type='button'
												className='dn-photo-nav-btn'
												disabled={!hasNext}
												onClick={() => selectPhoto(1)}
												aria-label={t('display.photosBtns.next')}
											>
												<DnIcons.arrow />
											</button>
										</div>
									</aside>
								)}
							</div>
						</header>

						{/* Image Info */}
						<section id='dn-info' className='dn-card'>
							<div className='dn-section-header'>
								<div className='dn-section-title-row'>
									<h2 className='dn-section-title'>
										<DnIcons.image /> {t('display.imgInfo.title', { defaultValue: 'Image Information' })}
									</h2>
									{image_id && <IdChip label='Id' value={image_id} full />}
								</div>
							</div>
							<div className='dn-stats'>
								<Stat label={t('display.imgInfo.name', { defaultValue: 'Name' })} value={image_info?.filename} />
								{image_info?.original && <Stat label={t('display.imgInfo.original', { defaultValue: 'Original' })} value={image_info.original} mono />}
								{globalTagsOpts.length > 0 && (
									<Stat label={t('display.imgInfo.globalTags', { defaultValue: 'Global tags' })} value={globalTagsOpts.join(', ')} />
								)}
								{resourceTagsOpts.length > 0 && (
									<Stat label={t('display.imgInfo.resourceTags', { defaultValue: 'Resource tags' })} value={resourceTagsOpts.join(', ')} />
								)}
							</div>
						</section>

						{!image_error?.isError && imageReasonsCount > 0 && (
							<ReportBanner
								prefix='dn'
								ns='display'
								hasActiveTicket={hasActiveTicket}
								resolvedStatus={latestResolvedStatus}
								note={resolvedNote}
								onHistory={openTicketHistory}
								expired={reportWindow.expired}
								days={reportWindow.days}
								onReport={() => setReportOpen(true)}
							/>
						)}

						{/* Recognition (result selector + viewer + summary all in one) */}
						<section id='dn-recon' className='dn-card dn-card--flush'>
							<div className='dn-recon-header'>
								<div className='dn-recon-header-main'>
									<h2 className='dn-section-title'>
										<DnIcons.tag /> {t('display.recognition.title', { defaultValue: 'Recognition' })}
									</h2>
								</div>
								{isAdminUser && !image_error?.isError && result_history.length > 0 && (
									<div className='dn-recon-history'>
										<DnIcons.history />
										<select
											className='dn-history-select'
											value={historyId}
											onChange={(e) => { setRecon(''); setResultId(''); setHistoryId(e.target.value); }}
											disabled={result_history_loading}
											aria-label={t('display.resultHistory', { defaultValue: 'Result History' })}
										>
											<option value=''>{result_history_loading ? t('display.loading', { defaultValue: 'Loading…' }) : t('display.resultHistory', { defaultValue: 'Result History' })}</option>
											{result_history.map((h) => (
												<option key={h._id.$oid} value={h._id.$oid}>{h.name}</option>
											))}
										</select>
									</div>
								)}
							</div>

							{image_info?.status === 7 && (
								<div className='dn-recon-banner dn-recon-banner--info'>
									<div className='dn-recon-banner-icon'>
										<DnIcons.cloudDownload />
									</div>
									<div className='dn-recon-banner-body'>
										<div className='dn-recon-banner-title'>{t('display.archiveTitle', { defaultValue: 'Image from cloud archive' })}</div>
										<div className='dn-recon-banner-text'>{t('display.alertArchive')}</div>
									</div>
								</div>
							)}

							{image_error?.isError && image_error?.type === 'image_result' ? (
								<div className='dn-recon-banner dn-recon-banner--warn'>
									<div className='dn-recon-banner-icon'>
										<DnIcons.scan />
									</div>
									<div className='dn-recon-banner-body'>
										<div className='dn-recon-banner-title'>{t('display.alertTitle', { defaultValue: 'No recognition available' })}</div>
										<div className='dn-recon-banner-text'>{t('display.alert')}</div>
									</div>
								</div>
							) : (
								(resultTypeOpts.length > 1 || selectedResult.type === 1) && (
									<div className='dn-recon-toolbar'>
										{resultTypeOpts.length > 1 && (
											<div className='dn-recon-toolbar-group'>
												<span className='dn-toolbar-label'>{t('display.result.typesLabel', { defaultValue: 'Result types' })}</span>
												<div className='dn-recon-toggle'>
													{resultTypeOpts.map((o) => (
														<label key={o._id.$oid} className={`dn-pill${resultId === o._id.$oid ? ' active' : ''}`}>
															<input
																type='radio'
																name='resultType'
																value={o._id.$oid}
																checked={resultId === o._id.$oid}
																onChange={(e) => { setRecon(''); setHistoryId(''); setResultId(e.target.value); }}
															/>
															{o.name}
														</label>
													))}
												</div>
											</div>
										)}

										{resultTypeOpts.length > 1 && selectedResult.type === 1 && (
											<span className='dn-recon-toolbar-divider' aria-hidden='true' />
										)}

										{selectedResult.type === 1 && (
											<div className='dn-recon-toolbar-group'>
												<span className='dn-toolbar-label'>{t('display.recognition.showing', { defaultValue: 'Showing' })}</span>
												<div className='dn-recon-toggle'>
													{result_info.n_elements > 0 && (
														<label className={`dn-pill${recon === 'elements' ? ' active' : ''}`}>
															<input type='radio' name='recon' value='elements' checked={recon === 'elements'} onChange={(e) => setRecon(e.target.value)} />
															{t('display.resultInfo.elements', { defaultValue: 'Elements' })}
														</label>
													)}
													{result_info.n_predictions > 0 && (
														<label className={`dn-pill${recon === 'predictions' ? ' active' : ''}`}>
															<input type='radio' name='recon' value='predictions' checked={recon === 'predictions'} onChange={(e) => setRecon(e.target.value)} />
															{t('display.resultInfo.predictions', { defaultValue: 'Predictions' })}
														</label>
													)}
													{result_info.n_extras > 0 && (
														<label className={`dn-pill${recon === 'extras' ? ' active' : ''}`}>
															<input type='radio' name='recon' value='extras' checked={recon === 'extras'} onChange={(e) => setRecon(e.target.value)} />
															{t('display.resultInfo.extras', { defaultValue: 'Extras' })}
														</label>
													)}
												</div>
											</div>
										)}
									</div>
								)
							)}

							<div className='dn-recon-body'>
								<div className='dn-recon-frame' style={{ height: defaultHeight }}>
									{imageError.isError ? (
										<div className='dn-error'>
											<PnIcons.alert />
											<p>{t('display.errors.image', { defaultValue: 'Error loading image' })}</p>
											<p className='dn-error-sub'>{imageError.msg}</p>
										</div>
									) : (
										<>
											<Canvas
												id={1}
												height={defaultHeight}
												urlPhoto={image}
												canvas={canvas}
												setCanvas={setCanvas}
												tagsList={tagsList}
												predictions={predictions}
											/>
											<PictureButtons
												imageInfo={image_info}
												imageSrc={image}
												onFit={() => canvas?.setFitZoom()}
												onShowControls={() => setControlsOpen(true)}
											/>
										</>
									)}
								</div>
								<div className='dn-recon-side'>
									{isEmpty(selectedResult) ? (
										<div className='dn-recon-empty'>
											<DnIcons.list />
											<p>{t('display.recognition.empty', { defaultValue: 'Select a result type to see detected items.' })}</p>
										</div>
									) : (selectedResult.type === 1 || selectedResult.type === 3) ? (
										<TagList
											canvas={canvas}
											resultTagsLoading={result_tags_loading}
											appOrgConfig={appOrgConfig}
											tagsList={tagsList}
											selectedResult={selectedResult}
											imageInfo={image_info}
										/>
									) : (
										<div className='dn-recon-empty'>
											<DnIcons.list />
											<p>{t('display.recognition.unsupportedType', { defaultValue: 'No list view for this result type.' })}</p>
										</div>
									)}
								</div>
							</div>

							{result_info?.model && result_info?.version && (
								<div className='dn-recon-caption'>
									{t('display.recognition.generatedBy', { defaultValue: 'Generated by' })} ·{' '}
									<strong>{result_info.model.name}</strong> · v{' '}
									<strong>{result_info.version.name}</strong>
								</div>
							)}

							{isCustomValid(result_info) && (
								<ReconSummary resultInfo={result_info} />
							)}

							{(selectedResult.type === 1 || selectedResult.type === 3) && tagsList.length > 0 && (
								<TagsChart
									appOrgConfig={appOrgConfig}
									tagsList={tagsList}
									selectedResult={selectedResult}
									imageInfo={image_info}
								/>
							)}
						</section>

						<div className='dn-mobile-footer'>
							<button type='button' className='ds-tour-launcher' onClick={tour.start}>
								<DnIcons.compass /> {t('tour.launcher', { defaultValue: 'Take a tour' })}
							</button>
						</div>
					</main>
				</div>

				<ReportErrorModal
					open={reportOpen}
					onClose={() => setReportOpen(false)}
					imageId={image_id}
					resultId={resultId}
					organization={ticketOrgId}
				/>

				{/* Ticket transition log. Credits the note to Backoffice only on the rows
				    that carry one: the OPEN row is the reporter's own action, and the entry
				    never names the actor, so labelling every row would tell the client that
				    Backoffice filed their report. */}
				<HistoryDrawer
					open={ticketDrawer}
					onClose={() => setTicketDrawer(false)}
					history={ticketHistory}
					statusDesc={getTicketStatusDesc}
					eyebrow={trackedTicket?.code
						? t('display.reportError.banner.history.eyebrow', { code: trackedTicket.code })
						: t('display.reportError.banner.history.eyebrowFallback', { defaultValue: 'Ticket' })}
					title={t('display.reportError.banner.history.title', { defaultValue: 'Historial de estado' })}
					emptyText={t('display.reportError.banner.history.empty', { defaultValue: 'Sin historial disponible.' })}
					closeLabel={t('display.reportError.banner.history.close', { defaultValue: 'Cerrar historial' })}
					fromLabel=''
					author={(entry) => (entry?.comment
						? t('display.reportError.banner.history.author', { defaultValue: 'Backoffice' })
						: null)}
				/>

				<Tour
					steps={tourSteps}
					open={tour.isOpen}
					onClose={tour.stop}
				/>
			</div>
		</div>
	);
};

export default DisplayNew;
